import axios from "axios";
import { store } from "../store";

let installed = false;
let refreshPromise = null;

function accessToken() {
  const token = store.state.accessToken;
  return typeof token === "string" && token && token !== "null" && token !== "undefined"
    ? token : null;
}

function syncDefaultAuthorization() {
  const token = accessToken();
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
}

function setAuthorization(config, token) {
  config.headers ||= {};
  if (typeof config.headers.set === "function") {
    if (token) config.headers.set("Authorization", `Bearer ${token}`);
    else config.headers.delete("Authorization");
  } else {
    // Plain header objects are also supported.
    for (const key of Object.keys(config.headers)) {
      if (key.toLowerCase() === "authorization") delete config.headers[key];
    }
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
}

function tokenEndpoint(config) {
  const path = String(config?.url || "").split(/[?#]/)[0];
  return /(?:^|\/)token(?:\/refresh)?\/?$/.test(path);
}

export default function setup() {
  // Available synchronously, before the first request or router navigation.
  syncDefaultAuthorization();
  if (installed) return;
  installed = true;

  axios.interceptors.request.use(config => {
    syncDefaultAuthorization();
    // Login/refresh must not send an expired access token.
    setAuthorization(config, tokenEndpoint(config) ? null : accessToken());
    return config;
  }, undefined, { synchronous: true });

  axios.interceptors.response.use(response => response, async err => {
    if (!err.response) {
      return Promise.reject({ message: "Cannot connect to Server", code: 408 });
    }
    const request = err.config;
    const data = err.response.data || {};
    if (!request || request._retry || tokenEndpoint(request) || data.detail === "Unpermitted") {
      return Promise.reject(err);
    }

    const authFailure = err.response.status === 401 || (
      err.response.status === 403 && (
        data.detail === "Authentication credentials were not provided." ||
        data.code === "token_not_valid" || data.type === "application/json"
      )
    );
    if (!store.state.refreshToken || !authFailure) return Promise.reject(err);

    // One retry per request and only one refresh for concurrent API calls.
    request._retry = true;
    const sentAuthorization = typeof request.headers?.get === "function"
      ? request.headers.get("Authorization")
      : request.headers?.Authorization || request.headers?.authorization;
    const currentToken = accessToken();
    if (currentToken && sentAuthorization !== `Bearer ${currentToken}`) {
      // A concurrent request already refreshed the token after this call began.
      setAuthorization(request, currentToken);
      return axios(request);
    }

    if (!refreshPromise) {
      refreshPromise = Promise.resolve().then(() => store.dispatch("refreshToken"))
        .finally(() => { refreshPromise = null; });
    }
    await refreshPromise;
    syncDefaultAuthorization();
    setAuthorization(request, accessToken());
    return axios(request);
  });
}
